import { useMutation } from '@apollo/client'
import { CreatePolicyUserRoleDocument, ReplaceCustomRolePoliciesDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath, Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { SuspenseWithLoading } from '@salescore/client-common'
// eslint-disable-next-line sonarjs/deprecation
import type { useModal } from '@salescore/frontend-common'
import { message, Modal } from 'antd'
import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PolicyForm } from './PolicyForm'
import {
  buildActionsWithPermission,
  buildDefaultPolicyFormValue,
  PolicyFormSchema,
  type PolicyFormValue,
} from './policyFormValue'

// eslint-disable-next-line sonarjs/deprecation
export const PolicyCreateModal = ({ modal }: { modal: ReturnType<typeof useModal> }): ReactElement => {
  const { t } = useTranslation()

  return (
    <Modal
      open={modal.isModalVisible}
      onCancel={modal.hideModal}
      width={'60%'}
      cancelText={t(`閉じる`)}
      okButtonProps={{ style: { display: 'none' } }}
      destroyOnHidden
    >
      <SuspenseWithLoading>
        <Body
          onAfterFinish={() => {
            modal.hideModal()
          }}
        />
      </SuspenseWithLoading>
    </Modal>
  )
}

const Body = ({ onAfterFinish }: { onAfterFinish: () => void }): ReactElement => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [createPolicyUserRoleMutation] = useMutation(CreatePolicyUserRoleDocument)
  const [replaceCustomRolePoliciesMutation] = useMutation(ReplaceCustomRolePoliciesDocument)

  const onFinish = async (value: PolicyFormValue): Promise<void> => {
    const validated = PolicyFormSchema.safeParse(value)
    if (!validated.success) {
      void message.warning(t(`入力に誤りがあります`))
      return
    }

    setLoading(true)
    try {
      const result = await createPolicyUserRoleMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          policyUserRole: {
            label: validated.data.label,
            description: validated.data.description ?? '',
          },
        },
        refetchQueries: ['fetchPolicyUserRoles'],
      })

      const actionsWithPermission = buildActionsWithPermission(validated.data.actionsWithPermission)

      if (result.data?.createPolicyUserRole.name === undefined) {
        throw new Error(`権限セットを作成できませんでした`)
      } else {
        const customRolePolicies = await replaceCustomRolePoliciesMutation({
          variables: {
            organizationId: getOrganizationIdFromPath(),
            customRolePolicies: {
              userRole: result.data.createPolicyUserRole.name,
              actionsWithPermission,
            },
          },
          refetchQueries: ['fetchPolicies'],
        })
        Posthog.track(POSTHOG_EVENTS.save_custom_role, {
          organizationId: getOrganizationIdFromPath(),
          config: customRolePolicies.data?.replaceCustomRolePolicies,
        })
      }
      void message.success(t(`権限セットを作成しました`))
    } catch {
      void message.error(t(`エラーが発生しました`))
    } finally {
      onAfterFinish()
      setLoading(false)
    }
  }

  return (
    <PolicyForm onFinish={onFinish} loading={loading} initialValue={buildDefaultPolicyFormValue()} isEditing={false} />
  )
}
